import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import { GoldButton } from "../components/GoldButton";
import api from "../lib/api";

export default function ConseilDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [others, setOthers] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    api.article(slug).then(setArticle).catch(() => setNotFound(true));
    api.articles()
      .then((r) => setOthers((r.articles || []).filter((a) => a.slug !== slug).slice(0, 2)))
      .catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl px-5 pt-40 pb-28 text-center">
        <h1 className="font-serif text-4xl text-white">Article introuvable</h1>
        <Link to="/conseils" className="mt-6 inline-flex items-center gap-2 text-gold hover:underline"><ArrowLeft className="h-4 w-4" /> Retour aux conseils</Link>
      </div>
    );
  }
  if (!article) return <div className="pt-40 pb-28 text-center text-slate-500">Chargement…</div>;

  const paragraphs = (article.content || "").split("\n").filter((p) => p.trim());

  return (
    <div data-testid="conseil-detail-page" className="pt-32">
      <Seo title={article.title} path={`/conseils/${article.slug}`} description={article.excerpt} />
      <article className="mx-auto max-w-3xl px-5 lg:px-8">
        <Link to="/conseils" data-testid="back-to-conseils" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold">
          <ArrowLeft className="h-4 w-4" /> Tous les conseils
        </Link>
        <Reveal className="mt-6">
          <Overline>{article.tag}</Overline>
          <h1 className="mt-4 font-serif text-4xl leading-tight tracking-tight text-white md:text-5xl">{article.title}</h1>
          <p className="mt-4 flex items-center gap-1.5 text-sm text-slate-500"><Clock className="h-4 w-4" /> {article.read} de lecture</p>
        </Reveal>
        {article.cover && (
          <Reveal delay={0.05} className="mt-8 overflow-hidden rounded-3xl border border-white/10">
            <img src={article.cover} alt={article.title} className="aspect-[16/8] w-full object-cover" />
          </Reveal>
        )}
        <Reveal delay={0.1} className="mt-10 space-y-5">
          {paragraphs.map((p, i) => (
            <p key={i} className="text-lg leading-relaxed text-slate-300">{p}</p>
          ))}
        </Reveal>

        <Reveal className="mt-12 rounded-3xl border border-gold/25 bg-navy-elevated p-8 text-center">
          <h3 className="font-serif text-2xl text-white">Un logement à suivre sereinement ?</h3>
          <p className="mt-2 text-slate-300">Estimez votre abonnement ou demandez un rendez-vous.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <GoldButton to="/tarifs">Estimer mon abonnement <ArrowRight className="h-4 w-4" /></GoldButton>
            <GoldButton to="/souscription" variant="outline">Demander un rendez-vous</GoldButton>
          </div>
        </Reveal>
      </article>

      {others.length > 0 && (
        <section className="mx-auto max-w-3xl px-5 py-16 lg:px-8">
          <Overline>À lire aussi</Overline>
          <div className="mt-6 space-y-3">
            {others.map((a) => (
              <Link key={a.slug} to={`/conseils/${a.slug}`} className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-navy-surface/60 p-5 hover:border-gold/40">
                <div>
                  <span className="text-xs uppercase tracking-wide text-gold">{a.tag}</span>
                  <p className="text-slate-100">{a.title}</p>
                </div>
                <ArrowRight className="h-4 w-4 flex-shrink-0 text-gold" />
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
